import { t as localeText } from "@/shared/lib/translate";
import { useEffect, useRef, useState } from "react";
import { getAsset } from "./asset-actions";
import { getGeneration } from "./generations";
import { getGenerationAuthority } from "./generation-authority";
import type { components } from "./contract";
type V2Asset = components["schemas"]["V2Asset"];
type V2Generation = components["schemas"]["V2Generation"];
type V2GenerationAuthority = Awaited<ReturnType<typeof getGenerationAuthority>>;

export type CreationDestination = "image" | "video" | "audio" | "3d";
export type CreationHandoffKind = "edit" | "reference" | "style" | "recreate" | "regenerate";
export interface CreationHandoff { kind: CreationHandoffKind; asset?: V2Asset; generation?: V2Generation; authority?: V2GenerationAuthority; model?: string }
const key = (space: string, media: string) => `afterform:creation-handoff:${space}:${media}`;
export const CREATION_NAVIGATION_EVENT = "afterform:creation-navigation";
const HANDOFF_EVENT = "afterform:creation-handoff";
export interface CreationNavigation { spaceID: string; media: CreationDestination; search: Record<string, string> }
type CreationNavigator = (destination: CreationNavigation) => Promise<unknown>;
let navigateCreation: CreationNavigator | undefined;
const navigating = new Map<string, string>();

/** The app owns routing; API helpers never replace the document. */
export function registerCreationNavigator(navigate: CreationNavigator): () => void {
  const previous = navigateCreation;
  navigateCreation = navigate;
  return () => { if (navigateCreation === navigate) navigateCreation = previous; };
}

function notifyHandoff(spaceID: string, media: CreationDestination, error?: string) {
  window.dispatchEvent(new CustomEvent(HANDOFF_EVENT, { detail: { spaceID, media, error } }));
}

/** Store identities only. The destination rechecks access before using a source. */
export function openCreation(spaceID: string, media: CreationDestination, kind: CreationHandoffKind, assetID?: string, generationID?: string, model?: string, audioOperation: "text_to_speech" | "music_generate" | "audio_generate" | "sound_generate" | "voice_isolate" = "text_to_speech"): void {
  if (!navigateCreation) throw new Error(localeText("interface.creation_navigation_is_not_ready_54a896b6", { defaultValue: "Creation navigation is not ready." }));
  const storageKey = key(spaceID, media);
  const requestID = crypto.randomUUID();
  const search: Record<string, string> = {};
  if (media === "image" && kind === "edit" && assetID) {
    sessionStorage.removeItem(storageKey);
    search.tool = "edit";
    search.editorAsset = assetID;
  } else {
    sessionStorage.setItem(storageKey, JSON.stringify({ kind, assetID, generationID, model, created: Date.now(), requestID }));
    if (kind === "edit") search.tool = "edit";
    if (media === "audio") { search.audio_group = audioOperation === "voice_isolate" ? "audio_editing" : audioOperation === "music_generate" ? "music" : (audioOperation === "audio_generate" || audioOperation === "sound_generate") ? "sound_effects" : "voice"; search.audio_tool = audioOperation; }
    if (media === "video") search.mode = "image_to_video";
    if (media === "3d") search.tool = "image_to_mesh";
  }
  navigating.set(storageKey, requestID);
  // Persistent composers must honor explicit handoffs instead of restoring a
  // previously visited tool. Notify before the router changes the active media.
  window.dispatchEvent(new CustomEvent<CreationNavigation>(CREATION_NAVIGATION_EVENT, { detail: { spaceID, media, search } }));
  notifyHandoff(spaceID, media);
  void navigateCreation({ spaceID, media, search }).then(() => {
    if (navigating.get(storageKey) !== requestID) return;
    navigating.delete(storageKey);
    notifyHandoff(spaceID, media);
  }, error => {
    if (navigating.get(storageKey) !== requestID) return;
    navigating.delete(storageKey);
    sessionStorage.removeItem(storageKey);
    notifyHandoff(spaceID, media, error instanceof Error ? error.message : "Couldn’t open this creation.");
  });
}

export function useCreationHandoff(spaceID: string, media: CreationDestination, ready: boolean,
  apply: (handoff: CreationHandoff) => void, onError: (message: string) => void) {
  const callbacks = useRef({ apply, onError });
  callbacks.current = { apply, onError };
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const receive = (event: Event) => {
      const detail = (event as CustomEvent<{ spaceID: string; media: CreationDestination; error?: string }>).detail;
      if (detail.spaceID !== spaceID || detail.media !== media) return;
      setRevision(value => value + 1);
      if (detail.error && ready) callbacks.current.onError(detail.error);
    };
    window.addEventListener(HANDOFF_EVENT, receive);
    return () => window.removeEventListener(HANDOFF_EVENT, receive);
  }, [spaceID, media, ready]);
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const storageKey = key(spaceID, media);
    if (navigating.has(storageKey)) return;
    let raw: string | null;
    try { raw = sessionStorage.getItem(storageKey); } catch { return; }
    if (!raw) return;
    const isCurrent = () => !cancelled && !navigating.has(storageKey) && sessionStorage.getItem(storageKey) === raw;
    void (async () => {
      try {
        const value = JSON.parse(raw);
        if (!["edit", "reference", "style", "recreate", "regenerate"].includes(value.kind) || !Number.isFinite(value.created) || Date.now() - value.created > 300_000) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        const [asset, generation] = await Promise.all([
          typeof value.assetID === "string" ? getAsset(value.assetID) : undefined,
          typeof value.generationID === "string" ? getGeneration(value.generationID) : undefined,
        ]);
        if (!isCurrent()) return;
        if ((asset && (asset.space_id !== spaceID || asset.status !== "available" || asset.integrity_state !== "verified")) ||
          (generation && generation.space_id !== spaceID)) throw new Error(localeText("interface.this_creation_is_no_longer_available_in_this_wor_d8e2b8a8", { defaultValue: "This creation is no longer available in this workspace." }));
        const authority = generation ? await getGenerationAuthority(generation.id) : undefined;
        if (!isCurrent()) return;
        callbacks.current.apply({ kind: value.kind, asset, generation, authority, model: typeof value.model === "string" ? value.model : undefined });
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        if (isCurrent()) {
          sessionStorage.removeItem(storageKey);
          callbacks.current.onError(error instanceof Error ? error.message : "Couldn’t open this creation.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [spaceID, media, ready, revision]);
}
